import { useState } from 'react'
import { Modal } from './Modal'
import { PhoneMultiSelect } from './PhoneMultiSelect'
import {
  useAccessKey, usePhones,
  useUpdateAccessKeyLabel, useAttachPhone, useDetachPhone,
} from '../api'
import { useNotify } from './Notifications'

const LABEL_MAX = 100

interface EditAccessKeyDialogProps {
  open: boolean
  userId: string | null
  onClose: () => void
}

/**
 * Edit modal — label + attach/detach. Attach/detach fire one request each
 * (relative to the current server-side phone list), so ordering is
 * idempotent and partial failures leave a sane state.
 *
 * After mutations succeed we surface the "agent reconnect within ~30s"
 * info-toast from the plan: the relay already holds the stale phone→userId
 * mapping from a prior `/internal/phones/verify`, so live WS events will
 * lag REST until the agent reconnects.
 */
export function EditAccessKeyDialog({ open, userId, onClose }: EditAccessKeyDialogProps) {
  const { data: detail } = useAccessKey(userId)
  const { data: phones = [] } = usePhones()
  const notify = useNotify()

  const updateLabel = useUpdateAccessKeyLabel()
  const attach = useAttachPhone()
  const detach = useDetachPhone()

  // React 19 "reset state when props change" pattern — preferred over
  // useEffect+setState, which the lint rule flags as a cascading render.
  // `hydratedFor` tracks which detail.id we've already copied into local
  // state; when it flips, we re-seed in render.
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  const [label, setLabel] = useState('')
  const [selected, setSelected] = useState<string[]>([])

  if (detail && detail.id !== hydratedFor) {
    setHydratedFor(detail.id)
    setLabel(detail.label ?? '')
    setSelected(detail.phone_ids)
  }

  const close = () => {
    updateLabel.reset()
    attach.reset()
    detach.reset()
    onClose()
  }

  const labelTooLong = label.length > LABEL_MAX
  const pending = updateLabel.isPending || attach.isPending || detach.isPending

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!detail || !userId || selected.length === 0 || labelTooLong) return

    const current = new Set(detail.phone_ids)
    const next = new Set(selected)
    const toAttach = selected.filter(id => !current.has(id))
    const toDetach = detail.phone_ids.filter(id => !next.has(id))
    const newLabel = (label.trim() || null)
    const labelChanged = newLabel !== (detail.label ?? null)

    try {
      if (labelChanged) {
        await updateLabel.mutateAsync({ id: userId, label: newLabel })
      }
      // TODO(PR2): replace these two sequential loops with a single
      // atomic `PUT /api/v1/access-keys/{id}/phones` call that takes
      // the full desired phone_ids set. The current approach is two
      // independent HTTP requests per changed phone, each in its own
      // transaction. Partial failure leaves the access key in a hybrid
      // state and `notify(err.message)` can't tell which phone broke.
      // A concurrent admin edit can race mid-loop. Plan:
      // docs/open/phone-admin-response-split.md (atomic PUT section).
      for (const phoneId of toAttach) {
        await attach.mutateAsync({ userId, phoneId })
      }
      for (const phoneId of toDetach) {
        await detach.mutateAsync({ userId, phoneId })
      }
      if (toAttach.length > 0 || toDetach.length > 0) {
        notify('Агент подхватит изменения при следующем reconnect\'е (до 30 сек)', 'info')
      }
      close()
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Ошибка сохранения', 'error')
    }
  }

  if (!detail) {
    return (
      <Modal open={open} onClose={close} title="Ключ доступа">
        <p className="text-sm text-gray-500 dark:text-gray-400">Загрузка…</p>
      </Modal>
    )
  }

  return (
    <Modal open={open} onClose={close} title="Редактировать ключ">
      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Метка
          </label>
          <input
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Необязательно"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {labelTooLong && (
            <p className="text-xs text-red-500 mt-1">
              Не длиннее {LABEL_MAX} символов
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Трубки <span className="text-red-500">*</span>
          </label>
          <PhoneMultiSelect
            phones={phones}
            keepIds={detail.phone_ids}
            selected={selected}
            onChange={setSelected}
            disabled={pending}
          />
          {selected.length === 0 && (
            <p className="text-xs text-red-500 mt-1">
              Оставить ключ без трубок нельзя
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={close}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={pending || selected.length === 0 || labelTooLong}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
          >
            {pending ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
